import React, { useEffect } from 'react';
import { Modal } from 'antd';
import styled from 'styled-components';
import { Heading } from 'components/heading/Heading';
import { Text } from 'components/text/Text';
import { BadgePrimary } from 'components/badge/BadgePrimary';
import { PrimaryButton } from 'components/buttons/primary-button/primary.button';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { colors } from 'styles/colors';
import { SafeMarkdown } from './safe-markdown';
import { useBannerDismissal } from './use-banner-dismissal';
import { getHasEliteTutorRight } from 'logic/store/authentication/authentication.selectors';
import { getActiveBanner } from 'logic/store/elitetutor/elitetutor.selectors';
import { elitetutorSlice } from 'logic/store/elitetutor/elitetutor.slice';
import { ElitetutorNewsBanner } from 'logic/api-models/api-models';
import { useDispatch, useSelector } from 'react-redux';
import { CONTACT_CAROLINE } from 'assets/images/images';
import { Breakpoints } from 'logic/hooks/use-breakpoints';

const Card = styled.div`
  background-color: ${colors.violetElite};
  border-radius: 16px;
  padding: 30px 24px;
  color: ${colors.white};
`;

const BannerModal = styled(Modal)`
  .ant-modal-content {
    background: transparent;
    box-shadow: none;
  }

  .ant-modal-body {
    padding: 0;
  }
`;

const AvatarFrame = styled.div`
  width: 126px;
  height: 126px;
  border-radius: 50%;
  border: 3px solid ${colors.white};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
`;

const Avatar = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
`;

const Content = styled.div`
  color: ${colors.transparentWhite};

  p {
    margin: 0 0 8px;
    line-height: 1.5;
    font-size: 15px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  strong {
    font-weight: 700;
  }

  a {
    color: ${colors.primaryLight};
    text-decoration: underline;
  }
`;

const DismissButton = styled(PrimaryButton)`
  && {
    background-color: ${colors.white};
    border-color: ${colors.white};
    color: ${colors.violetElite};
  }

  &&:hover,
  &&:focus,
  &&:active {
    background-color: ${colors.white};
    border-color: ${colors.white};
    color: ${colors.violetElite};
    opacity: 0.92;
  }
`;

const AvatarAndContentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  /* On mobile, stack the avatar on top of the content instead of side by side. */
  @media (${Breakpoints.maxTablet}) {
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }
`;

// Dark-rose pill with light-rose, bold text. Reuses the established BadgePrimary
// (pink background + rounded corners) and only overrides the text color and font
// size to match the design and the banner content size.
const CaroPill = styled(BadgePrimary)`
  && {
    font-size: 15px;
    padding: 2px 14px;
    margin-bottom: 8px;
  }
`;

const ContentColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  width: 100%;
  min-width: 0;

  /* On mobile, center everything (pill, heading, text, button) instead of left-aligning. */
  @media (${Breakpoints.maxTablet}) {
    align-items: center;
    text-align: center;
  }
`;

const BannerHeading = styled(Heading)`
  /* Heading hardcodes 'margin-bottom: 20px' via '&.ant-typography'. We match that
     specificity with '&&.ant-typography' so the override wins, but scoped only to
     this styled wrapper — the shared Heading component is untouched, so no other
     part of the app regresses. */
  &&.ant-typography {
    margin: 0;
  }
`;

const BannerContent = styled(Content)`
  margin-top: 0px;
`;

const BannerDismissButton = styled(DismissButton)`
  margin-top: 8px;
`;

interface AnnouncementBannerViewProps {
  banner: ElitetutorNewsBanner;
  onDismiss: () => void;
}

/** Presentational, side-effect-free card. */
export const AnnouncementBannerView: React.FC<AnnouncementBannerViewProps> = ({
  banner,
  onDismiss,
}) => (
  <Card>
    <AvatarAndContentRow>
      <AvatarFrame>
        <Avatar src={CONTACT_CAROLINE} alt="Caroline" />
      </AvatarFrame>
      <ContentColumn>
        <CaroPill>{translate(translationKeys.announcementBanner.badgeLabel)}</CaroPill>
        <BannerHeading size={20} color={colors.white}>
          {translate(translationKeys.announcementBanner.title)}
        </BannerHeading>

        <BannerContent>
          <SafeMarkdown content={banner.content} />
        </BannerContent>
        <BannerDismissButton onClick={onDismiss}>
          {translate(translationKeys.announcementBanner.dismissButton)}
        </BannerDismissButton>
        <Text size={12} color="transparentWhite">
          {translate(translationKeys.announcementBanner.dismissHint)}
        </Text>
      </ContentColumn>
    </AvatarAndContentRow>
  </Card>
);

/**
 * Global announcement banner for Premium (Elite) Tutors.
 *
 * The banner is fetched via the elitetutor store (fetchActiveBanner epic) and
 * read from the getActiveBanner selector. It is shown on every visit until the
 * tutor explicitly confirms it; the "read" state is persisted in LocalStorage
 * via useBannerDismissal (single key holding the last dismissed id), so a new
 * banner id from the backend resets visibility automatically.
 */
export const AnnouncementBanner: React.FC = () => {
  const dispatch = useDispatch();
  const isEliteTutor = useSelector(getHasEliteTutorRight);
  const banner = useSelector(getActiveBanner);
  const { isDismissed, dismiss } = useBannerDismissal(banner?._id);

  useEffect(() => {
    if (isEliteTutor) {
      dispatch(elitetutorSlice.actions.fetchActiveBanner());
    }
  }, [dispatch, isEliteTutor]);

  if (!isEliteTutor || !banner || isDismissed) {
    return null;
  }

  return (
    // Confirmation can only happen via the button: disable mask click, Escape and
    // the close icon so the tutor cannot dismiss (and persist "read") by accident.
    <BannerModal
      visible
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      width={880}
      destroyOnClose
    >
      <AnnouncementBannerView banner={banner} onDismiss={dismiss} />
    </BannerModal>
  );
};
